import axios from 'axios';

/**
 * LogService centralizza tutte le funzionalità di logging dell'applicazione
 * Traccia le azioni dell'utente e le invia al backend per l'archiviazione
 */

// Livelli di log
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  ERROR = 'error',
}

// Categorie delle azioni
export enum ActionCategory {
  AUTH = 'authentication',
  NAVIGATION = 'navigation',
  SEARCH = 'search',
  FILTER = 'filter',
  USER_ACTION = 'user_action',
  SYSTEM = 'system',
}

// Informazioni base sull'utente
interface UserInfo {
  id?: string;
  username?: string;
  email?: string;
  roles?: string[];
}

// Struttura dei messaggi di log
interface LogMessage {
  action: string;
  description: string;
  timestamp: string;
  userInfo?: UserInfo;
  metadata?: Record<string, any>;
  pagePath?: string;
  browser?: string;
  device?: string;
  sessionId?: string;
}

// Configurazione del servizio di logging
interface LogServiceConfig {
  serviceName: string;
  includeUserInfo: boolean;
  includeMetadata: boolean;
  includeBrowserInfo: boolean;
}

class LogService {
  private config: LogServiceConfig = {
    serviceName: 'cediFE',
    includeUserInfo: true,
    includeMetadata: true,
    includeBrowserInfo: true,
  };

  private userInfo: UserInfo = {};
  private sessionId: string = this.generateSessionId(); 
  private sessionStartTime: number = Date.now();

  constructor() {
    // Inizializza il servizio
    this.setupBeforeUnloadHandler();
  }

  /**
   * Genera un ID sessione univoco
   */
  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
  }

  /**
   * Imposta handler per l'evento beforeunload e altri eventi di chiusura
   */
  private setupBeforeUnloadHandler() {
    // Handler per beforeunload (chiusura finestra/tab)
/*     window.addEventListener('beforeunload', () => {
      this.logSessionEnd();
    }); */
    
    // Handler per visibilitychange (per catturare quando l'utente chiude il tab)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Potrebbe essere la navigazione via router, ma memorizziamo comunque
        // lo stato corrente della sessione
        const sessionDuration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
        localStorage.setItem('last_session_state', JSON.stringify({
          sessionId: this.sessionId,
          sessionDuration,
          startTime: new Date(this.sessionStartTime).toISOString(),
          userInfo: this.userInfo,
          timestamp: new Date().toISOString(),
          path: window.location.pathname
        }));
      }
    });
    
    // Gestione esplicita del logout
    const originalLogout = window.logout || function(){};
    window.logout = () => {
      // Eseguiamo il log esplicito prima del logout
   //   this.logSessionEnd();
      // Poi eseguiamo la funzione originale di logout
      originalLogout();
    };
  }

  /**
   * Inizializza il logger con le informazioni utente
   */
  public init(user: any) {
    // Verifica se c'è stato un cambio utente
    const prevUserId = this.userInfo?.id;
    const newUserId = user?.sub || user?.id;
    const isUserChange = prevUserId && newUserId && prevUserId !== newUserId;

    // Se c'è stato un cambio utente, salva il log di logout per l'utente precedente
/*     if (isUserChange) {
      console.log('User change detected, logging out previous user', prevUserId);
      this.logSessionEnd();
    } */
    
    if (user) {
      this.userInfo = {
        id: user.sub || user.id,
        username: user.preferred_username || user.username || user.email,
        email: user.email,
        roles: user.roles || [],
      };

      console.log('LogService initialized with user:', this.userInfo);
    }
    
    this.sessionStartTime = Date.now();
    this.sessionId = this.generateSessionId(); 
    
    // Salva sessionId e timestamp per il logout
    localStorage.setItem('session_id', this.sessionId);
    localStorage.setItem('login_timestamp', this.sessionStartTime.toString());
    
    // Log dell'evento di login
    this.log(
      LogLevel.INFO,
      ActionCategory.AUTH,
      'login',
      'User logged in',
      { 
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        userInfo: this.userInfo,
        logType: 'login' // Per esplicita categorizzazione
      }
    );
    
    // Controlla e invia eventuali log di logout pendenti
    // Facciamo un breve ritardo per assicurarci che sia completato il login
    setTimeout(() => {
      this.checkPendingLogoutLog();
    }, 1000);

    return true;
  }
  
  /**
   * Controlla e invia log pendenti (logout e navigazione)
   */
  private checkPendingLogoutLog() {
    try {
      // Controlla log di logout pendenti
      const pendingLogoutStr = localStorage.getItem('pending_logout_log');
      if (pendingLogoutStr) {
        console.log('Found pending logout log, sending now');
        
        // Ottieni il log di logout salvato
        const logoutData = JSON.parse(pendingLogoutStr);
        
        // Costruisci il payload per il server
        const payload = {
          level: 'info',
          serviceName: this.config.serviceName,
          message: logoutData
        };
        
        // Invia al server con più tentativi
        this.sendPendingLogoutWithRetry(payload, 3);
      }
      
      // Controlla log di navigazione pendenti
      const pendingNavigationsStr = localStorage.getItem('pending_navigation_logs');
      if (pendingNavigationsStr) {
        try {
          const pendingNavigations = JSON.parse(pendingNavigationsStr);
          if (pendingNavigations.length > 0) {
            console.log(`Found ${pendingNavigations.length} pending navigation logs, sending now`);
            
            // Invia ogni log di navigazione separatamente
            for (const navLog of pendingNavigations) {
              const payload = {
                level: 'info',
                serviceName: this.config.serviceName,
                message: navLog
              };
              
              this.sendPendingLogWithRetry(payload, 2, 'navigation');
            }
          }
        } catch (e) {
          console.error('Error processing pending navigation logs:', e);
          localStorage.removeItem('pending_navigation_logs'); // Cancella per evitare errori ricorrenti
        }
      }
    } catch (e) {
      console.error('Error processing pending logs:', e);
      localStorage.removeItem('pending_logout_log'); // Cancella per evitare errori ricorrenti
    }
  }
  
  /**
   * Invia il log di logout pendente con tentativi multipli
   */
  private sendPendingLogoutWithRetry(payload: any, retryCount: number) {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No auth token found for pending logout, will try again later');
      return; // Mantieni il log in localStorage per un tentativo futuro
    }
    
    axios.post('/nodeapi/writeLog', payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then(() => {
      console.log('Pending logout log sent successfully');
      localStorage.removeItem('pending_logout_log');
    })
    .catch(error => {
      console.error('Error sending pending logout log:', error);
      if (retryCount > 0) {
        console.log(`Retrying... (${retryCount} attempts left)`);
        setTimeout(() => {
          this.sendPendingLogoutWithRetry(payload, retryCount - 1);
        }, 1000); // Ritenta dopo 1 secondo
      }
    });
  }
  
  /**
   * Invia log pendenti (generici) con tentativi multipli
   */
  private sendPendingLogWithRetry(payload: any, retryCount: number, logType: string) {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn(`No auth token found for pending ${logType} log`);
      return; // Mantieni il log in localStorage per un tentativo futuro
    }
    
    axios.post('/nodeapi/writeLog', payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then(() => {
      console.log(`Pending ${logType} log sent successfully`);
      if (logType === 'navigation') {
        const pendingNavigations = JSON.parse(localStorage.getItem('pending_navigation_logs') || '[]');
        if (pendingNavigations.length > 0) {
          // Rimuovi il primo elemento (quello appena inviato)
          pendingNavigations.shift();
          localStorage.setItem('pending_navigation_logs', JSON.stringify(pendingNavigations));
        } else {
          localStorage.removeItem('pending_navigation_logs');
        }
      }
    })
    .catch(error => {
      console.error(`Error sending pending ${logType} log:`, error);
      if (retryCount > 0) {
        console.log(`Retrying... (${retryCount} attempts left)`);
        setTimeout(() => {
          this.sendPendingLogWithRetry(payload, retryCount - 1, logType);
        }, 1000); // Ritenta dopo 1 secondo
      }
    });
  }

  /**
   * Traccia un'azione utente a livello INFO
   */
  public logAction(
    category: ActionCategory,
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) {
    return this.log(LogLevel.INFO, category, action, description, metadata);
  }

  /**
   * Traccia eventi di navigazione
   */
  public logNavigation(
    page: string,
    description: string,
    metadata?: Record<string, any>
  ) {
    return this.log(
      LogLevel.INFO,
      ActionCategory.NAVIGATION,
      'page_view',
      description,
      { ...metadata, page }
    );
  }

  /**
   * Traccia errori
   */
  public logError(
    action: string,
    error: Error | string,
    metadata?: Record<string, any>
  ) {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;

    return this.log(
      LogLevel.ERROR,
      ActionCategory.SYSTEM,
      action,
      errorMessage,
      { ...metadata, errorStack }
    );
  }

  /**
   * Traccia la fine della sessione
   */
/*   private logSessionEnd() {
    const sessionDuration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
    
    console.log('Logging session end:', {
      sessionId: this.sessionId,
      sessionDuration,
      startTime: new Date(this.sessionStartTime).toISOString(),
      userInfo: this.userInfo
    });
    
    const logMessage = {
      action: `${ActionCategory.AUTH}:logout`,
      description: 'Session ended',
      timestamp: new Date().toISOString(),
      pagePath: window.location.pathname,
      sessionId: this.sessionId,
      browser: this.getBrowser(),
      device: this.getDevice(),
      userInfo: this.userInfo,
      metadata: { 
        sessionId: this.sessionId,
        sessionDuration,
        startTime: new Date(this.sessionStartTime).toISOString(),
        userInfo: this.userInfo
      }
    };
    
    // Salva il log di logout in localStorage per essere inviato al prossimo login
    try {
    // Add explicit log categorization
    logMessage.metadata = {
        ...logMessage.metadata,
      logType: 'logout'
      };
          localStorage.setItem('pending_logout_log', JSON.stringify(logMessage));
          console.log('Saved logout information to localStorage for future sending');
        } catch (e) {
          console.error('Error saving logout info to localStorage:', e);
        }
    
    // Tenta comunque di inviare il log normalmente (potrebbe non completare)
    this.log(
      LogLevel.INFO,
      ActionCategory.AUTH,
      'logout',
      'Session ended',
      { 
        sessionId: this.sessionId,
        sessionDuration,
        startTime: new Date(this.sessionStartTime).toISOString(),
        userInfo: this.userInfo
      }
    );
  } */

  /**
   * Funzione core di logging
   */
  private async log(
    level: LogLevel,
    category: ActionCategory,
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) {
    try {
      const timestamp = new Date().toISOString();
      const pagePath = window.location.pathname;
      
      // Costruisce il messaggio di log
      const logMessage: LogMessage = {
        action: `${category}:${action}`,
        description,
        timestamp,
        pagePath,
        sessionId: this.sessionId,
      };

      // Aggiunge info browser se configurato
      if (this.config.includeBrowserInfo) {
        logMessage.browser = this.getBrowser();
        logMessage.device = this.getDevice();
      }

      // Aggiunge info utente se disponibili
      if (this.config.includeUserInfo && Object.keys(this.userInfo).length > 0) {
        logMessage.userInfo = this.userInfo;
      }

      // Aggiunge metadati se disponibili
      if (this.config.includeMetadata && metadata) {
        logMessage.metadata = metadata;
      }

      // Controlla se è autenticato per inviare il log al server
      const token = localStorage.getItem('token');
      if (token) {
        await this.sendLogToServer(level, logMessage);
      } else {
        // Se è una pagina di autenticazione senza token, salviamo il log in localStorage temporaneamente
        if (category === ActionCategory.NAVIGATION && metadata?.isAuthPage) {
          try {
            const pendingNavigations = JSON.parse(localStorage.getItem('pending_navigation_logs') || '[]');
            pendingNavigations.push(logMessage);
            localStorage.setItem('pending_navigation_logs', JSON.stringify(pendingNavigations.slice(-10))); // Mantieni solo gli ultimi 10
            console.log('Salvato log di navigazione in attesa di autenticazione');
          } catch (e) {
            console.error('Errore nel salvare il log di navigazione in localStorage:', e);
          }
        }
      }
      
      // Log su console per sviluppo
      if (process.env.NODE_ENV === 'development') {
        this.logToConsole(level, category, action, description, metadata);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to log event:', error);
      return false;
    }
  }

  /**
   * Invia log al server backend
   */
  private async sendLogToServer(level: LogLevel, logMessage: LogMessage) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token found, log may not be processed');
        return; // Non tentare di inviare se non c'è token
      }

      const payload = {
        level,
        serviceName: this.config.serviceName,
        message: logMessage,
      };

      // Crea una chiave unica basata sull'azione e sul timestamp per evitare coalescenza di log simili
      const debounceKey = `${logMessage.action}-${Date.now()}`;
      
      // In caso di navigazione, usiamo un debounce molto breve
      const debounceTime = logMessage.action.includes('navigation:page_view') ? 50 : 100;
      
      // Usa una chiamata API con debounce per evitare troppe richieste
      this.debounceApiCall(() => {
        axios.post('/nodeapi/writeLog', payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
        .then(() => {
          console.log(`Log inviato con successo: ${logMessage.action} - ${logMessage.pagePath}`);
        })
        .catch(error => {
          // Solo log dell'errore, non disturbiamo l'utente
          console.error('Error sending log to server:', error);
        });
      }, debounceTime, debounceKey); // Utilizziamo la chiave univoca
    } catch (error) {
      console.error('Error preparing log payload:', error);
    }
  }

  // Variabile per memorizzare i timeout di debounce
  private debounceTimers: Record<string, NodeJS.Timeout> = {};

  /**
   * Funzione di debounce per le chiamate API
   */
  private debounceApiCall(fn: Function, delay: number, key: string = 'log-api-call') {
    if (this.debounceTimers[key]) {
      clearTimeout(this.debounceTimers[key]);
    }
    this.debounceTimers[key] = setTimeout(() => {
      fn();
      delete this.debounceTimers[key];
    }, delay);
  }

  /**
   * Log su console per sviluppo
   */
  private logToConsole(
    level: LogLevel,
    category: ActionCategory,
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) {
    const prefix = `[${level.toUpperCase()}] [${category}:${action}]`;
    
    switch (level) {
      case LogLevel.ERROR:
        console.error(prefix, description, metadata || '');
        break;
      case LogLevel.INFO:
        console.info(prefix, description, metadata || '');
        break;
      case LogLevel.DEBUG:
        console.debug(prefix, description, metadata || '');
        break;
      default:
        console.log(prefix, description, metadata || '');
    }
  }

  /**
   * Rileva il browser
   */
  private getBrowser(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.indexOf('Firefox') > -1) {
      return 'Firefox';
    } else if (userAgent.indexOf('Chrome') > -1) {
      return 'Chrome';
    } else if (userAgent.indexOf('Safari') > -1) {
      return 'Safari';
    } else if (userAgent.indexOf('Edge') > -1) {
      return 'Edge';
    } else {
      return 'Unknown';
    }
  }

  /**
   * Rileva il dispositivo
   */
  private getDevice(): string {
    const userAgent = navigator.userAgent;
    
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    } else if (/android/i.test(userAgent)) {
      return 'Android';
    } else {
      return 'Desktop';
    }
  }
}

// Export singleton instance
export const logService = new LogService();
export default logService;
